import { useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { CHAIN_ACCENTS, EASE, entityAssetUrl, fmtPct, fmtUsd, pctColor } from "./nl";
import { useEntitySelection } from "./EntitySelectionContext";
import { NetworkNodePopover } from "./NetworkNodePopover";
import { entityTrack } from "@/lib/entities";

/* Aave Protocol Network — signature Entity Intelligence visualization.
   SVG + Motion. EDGE = DEPLOYMENT / ENTITY RELATIONSHIP · NEVER FUND FLOW.
   Motion Contract: staged enter (center → edges draw outward → nodes resolve),
   transient hover isolate + sticky click selection (cross-module), composed
   rest state with all priority nodes readable. Reduced motion: immediate
   resolved topology, short opacity changes only. Entrance gated by useInView. */

const DESKTOP_SLOTS = [
  { x: 126, y: 92 }, { x: 538, y: 82 }, { x: 578, y: 254 }, { x: 122, y: 300 },
];
const MOBILE_SLOTS = [
  { x: 72, y: 60 }, { x: 290, y: 68 }, { x: 304, y: 242 }, { x: 70, y: 260 },
];

const CENTER_D = { x: 333, y: 205 };
const CENTER_M = { x: 179, y: 168 };
const TOKEN_D = { x: 420, y: 350 };
const TOKEN_M = { x: 212, y: 302 };
const TOKEN_ID = "__token__";

interface NetNode {
  chain: string; share: number; tvl: number; r: number;
  x: number; y: number; accent: string; slugGuess: string;
}

const chainSlug = (name: string) =>
  ({ Binance: "bnb-chain", BSC: "bnb-chain", "OP Mainnet": "optimism" } as any)[name] ||
  name.toLowerCase().replace(/\s+/g, "-");

const Node = ({ n, active, dim, mobile, inView, enterDelay, onHover, onLeave, onCommit }: {
  n: NetNode; active: boolean; dim: boolean; mobile: boolean;
  inView: boolean; enterDelay: number;
  onHover: (c: string) => void; onLeave: () => void; onCommit: (c: string) => void;
}) => {
  const reduced = useReducedMotion();
  return (
    <motion.g initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}
      transition={{ duration: reduced ? 0.12 : 0.3, delay: reduced ? 0 : enterDelay, ease: EASE }}>
      <motion.g
        role="button" tabIndex={0}
        aria-label={`${n.chain} — ${n.share}% of Aave TVL, ${fmtUsd(n.tvl)}. Deployment relationship.`}
        data-testid={`network-node-${n.slugGuess}`}
        style={{ cursor: "pointer", outline: "none" }}
        onMouseEnter={() => onHover(n.chain)}
        onMouseLeave={onLeave}
        onFocus={() => onHover(n.chain)}
        onBlur={onLeave}
        onClick={() => onCommit(n.chain)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCommit(n.chain); } }}
        animate={{ opacity: dim ? 0.28 : 1, scale: active && !reduced ? 1.03 : 1 }}
        transition={{ duration: 0.22, ease: EASE }}>
        <circle cx={n.x} cy={n.y} r={n.r} fill="#0e0e16" stroke={n.accent}
          strokeWidth={active ? 2 : 1}
          style={active ? { filter: `drop-shadow(0 0 10px ${n.accent}66)` } : undefined} />
        <circle cx={n.x} cy={n.y} r={n.r * 0.56} fill="#e7eaf3" opacity={0.94} style={{ pointerEvents: "none" }} />
        <image href={entityAssetUrl("chain", n.slugGuess)}
          x={n.x - n.r * 0.52} y={n.y - n.r * 0.52} width={n.r * 1.04} height={n.r * 1.04}
          clipPath={`circle(${n.r * 0.52}px at ${n.r * 0.52}px ${n.r * 0.52}px)`}
          style={{ pointerEvents: "none" }} />
        <circle cx={n.x + n.r * 0.55} cy={n.y + n.r * 0.55} r={mobile ? 2.5 : 3} fill={n.accent} />
        <text x={n.x - n.r} y={n.y + n.r + (mobile ? 14 : 18)}
          className="nl-geist" fontSize={mobile ? 8 : 11} fontWeight={600} letterSpacing="0.01em"
          fill={active ? n.accent : "#ffffff"}>{n.chain}</text>
        <text x={n.x - n.r} y={n.y + n.r + (mobile ? 26 : 36)}
          className="nl-mono" fontSize={mobile ? 7 : 10} fontWeight={500} fill="#9696a8">{`${n.share}%`}</text>
      </motion.g>
    </motion.g>
  );
};

export const ProtocolNetwork = ({ overview }: { overview: any }) => {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const dWrapRef = useRef<HTMLDivElement>(null);
  const mWrapRef = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const { selectedChain, setSelectedChain } = useEntitySelection();
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [tokenSelected, setTokenSelected] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dist = overview.chain_distribution?.chains || [];
  const tok = overview.token;
  const name = overview.identity.name;

  const build = (slots: { x: number; y: number }[], mobile: boolean): NetNode[] =>
    dist.slice(0, 4).map((c: any, i: number) => {
      const domShare = dist[0]?.share_pct || 1;
      const base = mobile ? 17 : 21;
      const max = mobile ? 47 : 35;
      const r = base + (max - base) * Math.sqrt((c.share_pct || 0) / domShare);
      return { chain: c.chain, share: c.share_pct, tvl: c.tvl_usd, r: Math.round(r),
               ...slots[i], accent: CHAIN_ACCENTS[i], slugGuess: chainSlug(c.chain) };
    });

  const dNodes = useMemo(() => build(DESKTOP_SLOTS, false), [dist]);
  const mNodes = useMemo(() => build(MOBILE_SLOTS, true), [dist]);

  // Interaction model: hover/focus = transient popover + isolate; click/Enter
  // = sticky selection (cross-module). One NetworkNodePopover per layout,
  // anchored beside the active node. Rest state = composed, no popover.
  const activeId = hoverId || (tokenSelected ? TOKEN_ID : selectedChain) || null;
  const active = activeId;
  const userActive = !!activeId;
  const tokenActive = activeId === TOKEN_ID;
  const others = Math.max(0, (overview.metrics?.chain_count || dist.length) - 4);

  const cancelLeave = () => { if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; } };
  const onHover = (c: string) => { cancelLeave(); setHoverId(c); };
  const onLeave = () => { cancelLeave(); leaveTimer.current = setTimeout(() => setHoverId(null), 140); };
  const onCommit = (c: string) => {
    setTokenSelected(false);
    if (selectedChain === c) {
      // toggle-off: also drop transient hover so deselection is immediately
      // observable (pointer may still rest on the node; touch has no leave)
      setSelectedChain(null);
      cancelLeave();
      setHoverId(null);
    } else {
      setSelectedChain(c);
    }
    entityTrack("network_node_focus", overview.identity.entity_id, { chain: c });
  };
  const onCommitToken = () => {
    if (tokenSelected) {
      setTokenSelected(false);
      cancelLeave();
      setHoverId(null);
    } else {
      setTokenSelected(true);
      setSelectedChain(null);
    }
    entityTrack("network_node_focus", overview.identity.entity_id, { node: "token" });
  };

  const popoverFor = (nodes: NetNode[]) => {
    if (tokenActive && tok) {
      return {
        key: "token", anchor: null as any,
        data: {
          eyebrow: "ASSOCIATED TOKEN", title: tok.symbol || "Token", accent: "#8b5cff",
          rows: [
            { label: "PRICE", value: tok.price_usd != null ? `$${Number(tok.price_usd).toLocaleString("en-US", { maximumFractionDigits: 2 })}` : "—" },
            { label: "24H", value: fmtPct(tok.change_24h_pct), color: pctColor(tok.change_24h_pct) },
          ],
          link: tok.route ? {
            label: `VIEW ${tok.symbol || "TOKEN"} →`, to: tok.route,
            onClick: () => entityTrack("token_click", overview.identity.entity_id, { from: "network_popover" }),
          } : undefined,
        },
      };
    }
    const n = nodes.find((x) => x.chain === activeId);
    if (!n) return null;
    return {
      key: n.chain, anchor: { x: n.x, y: n.y, r: n.r },
      data: {
        eyebrow: "DEPLOYMENT CHAIN", title: n.chain, accent: n.accent,
        rows: [
          { label: `SHARE OF ${String(name).toUpperCase()} TVL`, value: `${n.share}%` },
          { label: "DEPLOYED TVL", value: fmtUsd(n.tvl) },
        ],
      },
    };
  };
  const dPop = popoverFor(dNodes);
  if (dPop && dPop.key === "token") dPop.anchor = { ...TOKEN_D, r: 27 };
  const mPop = popoverFor(mNodes);
  if (mPop && mPop.key === "token") mPop.anchor = { ...TOKEN_M, r: 21 };
  const dObstacles = useMemo(() => [...dNodes.map((n) => ({ x: n.x, y: n.y, r: n.r })), { ...TOKEN_D, r: 27 }], [dNodes]);
  const mObstacles = useMemo(() => [...mNodes.map((n) => ({ x: n.x, y: n.y, r: n.r })), { ...TOKEN_M, r: 21 }], [mNodes]);
  const reHover = (key: string) => onHover(key === "token" ? TOKEN_ID : key);

  const edge = (from: { x: number; y: number }, to: NetNode | { x: number; y: number; accent?: string }, i: number, dashed = false) => {
    const accent = (to as any).accent || "#8b5cff";
    const toSel = "chain" in (to as any) ? (to as any).chain === active : false;
    const dim = userActive && "chain" in (to as any) && !toSel;
    return (
      <motion.line key={i} x1={from.x} y1={from.y} x2={(to as any).x} y2={(to as any).y}
        stroke={accent} strokeWidth={toSel ? 1.6 : 1} strokeDasharray={dashed ? "4 5" : undefined}
        initial={reduced ? { opacity: 0 } : { pathLength: 0, opacity: 0 }}
        animate={inView ? { pathLength: 1, opacity: dim ? 0.14 : toSel ? 0.7 : 0.34 } : {}}
        transition={reduced ? { duration: 0.12 }
          : { pathLength: { duration: 0.44, delay: 0.36 + i * 0.055, ease: EASE },
              opacity: { duration: 0.22, delay: 0.36 + i * 0.055 } }} />
    );
  };

  const rings = (c: { x: number; y: number }, radii: number[], ops: number[]) =>
    radii.map((r, i) => (
      <motion.circle key={r} cx={c.x} cy={c.y} r={r} fill="none" stroke="#8b5cff" strokeWidth={1}
        initial={{ opacity: 0 }} animate={inView ? { opacity: ops[i] } : {}}
        transition={{ duration: 0.5, delay: reduced ? 0 : 0.3 + i * 0.08 }} />
    ));

  const center = (c: { x: number; y: number }, r: number, mobile: boolean) => (
    <motion.g initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: reduced ? 0.12 : 0.4, delay: reduced ? 0 : 0.24, ease: EASE }}>
      <defs>
        <radialGradient id={`nl-core-${mobile ? "m" : "d"}`}>
          <stop offset="0%" stopColor="#291c40" /><stop offset="70%" stopColor="#120f1f" /><stop offset="100%" stopColor="#05050a" />
        </radialGradient>
      </defs>
      <circle cx={c.x} cy={c.y} r={r} fill={`url(#nl-core-${mobile ? "m" : "d"})`} stroke="#8b5cff" strokeWidth={1.5}
        style={{ filter: "drop-shadow(0 0 24px rgba(111,44,244,0.35))" }} />
      <image href={entityAssetUrl("protocol", overview.identity.slug)}
        x={c.x - r * 0.42} y={c.y - r * 0.42} width={r * 0.84} height={r * 0.84}
        style={{ pointerEvents: "none" }} />
      <circle cx={c.x} cy={c.y + r + (mobile ? -1 : 0)} r={mobile ? 4.5 : 6} fill="#8b5cff" />
      <text x={c.x - (mobile ? 15 : 17)} y={c.y + r + (mobile ? 22 : 32)} className="nl-sg"
        fontSize={mobile ? 13 : 15} fontWeight={500} fill="#ffffff">{name}</text>
      <text x={c.x - (mobile ? 20 : 22)} y={c.y + r + (mobile ? 37 : 49)} className="nl-mono"
        fontSize={mobile ? 8 : 9} fontWeight={500} letterSpacing="0.18em" fill="#9696a8">PROTOCOL</text>
    </motion.g>
  );

  const tokenNode = (p: { x: number; y: number }, r: number, mobile: boolean) => (
    <motion.g initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}
      transition={{ duration: reduced ? 0.12 : 0.3, delay: reduced ? 0 : 0.46 + 4 * 0.055, ease: EASE }}>
      <motion.g role="button" tabIndex={0}
        aria-label={`${tok?.symbol || "token"} — associated token, distinct entity. Press Enter for details.`}
        data-testid="network-node-token" style={{ cursor: "pointer", outline: "none" }}
        onMouseEnter={() => onHover(TOKEN_ID)}
        onMouseLeave={onLeave}
        onFocus={() => onHover(TOKEN_ID)}
        onBlur={onLeave}
        onClick={onCommitToken}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCommitToken(); } }}
        animate={{ opacity: tokenActive ? 1 : userActive ? 0.6 : 1, scale: tokenActive && !reduced ? 1.03 : 1 }} // 0.6 tier: token stays semi-readable — associated entity, not an unrelated chain
        transition={{ duration: 0.25, ease: EASE }}>
        <circle cx={p.x} cy={p.y} r={r} fill="#0e0e16" stroke="#8b5cff" strokeWidth={tokenActive ? 2 : 1}
          style={tokenActive ? { filter: "drop-shadow(0 0 10px #8b5cff66)" } : undefined} />
        <circle cx={p.x} cy={p.y} r={r * 0.56} fill="#e7eaf3" opacity={0.94} style={{ pointerEvents: "none" }} />
        <image href={entityAssetUrl("token", (tok?.symbol || "").toLowerCase())}
          x={p.x - r * 0.52} y={p.y - r * 0.52} width={r * 1.04} height={r * 1.04}
          style={{ pointerEvents: "none" }} />
        <circle cx={p.x + r * 0.55} cy={p.y + r * 0.55} r={mobile ? 2.5 : 3} fill="#8b5cff" />
        <text x={p.x - r} y={p.y + r + (mobile ? 14 : 18)} className="nl-geist" fontSize={mobile ? 8 : 11}
          fontWeight={600} fill={tokenActive ? "#8b5cff" : "#ffffff"}>{tok?.symbol}</text>
        <text x={p.x - r} y={p.y + r + (mobile ? 26 : 36)} className="nl-mono" fontSize={mobile ? 7 : 10}
          fontWeight={500} fill="#9696a8">TOKEN</text>
      </motion.g>
    </motion.g>
  );

  return (
    <div ref={ref} data-testid="protocol-network">
      <p className="nl-geist text-[10px] leading-[14px] font-semibold tracking-[1px] text-[#8b5cff] uppercase">
        SIGNATURE VISUAL&nbsp;&nbsp;·&nbsp;&nbsp;DEPLOYMENT TOPOLOGY
      </p>
      <h2 className="nl-sg text-[24px] leading-[30px] md:text-[28px] md:leading-[34px] font-medium tracking-[-0.005em] text-white mt-[10px]">
        {name} Protocol Network
      </h2>
      <p className="nl-mono text-[9px] leading-[14px] font-medium tracking-[0.01em] text-[#9696a8] mt-[10px]"
        data-testid="network-semantic-rule">
        EDGE = DEPLOYMENT / ENTITY RELATIONSHIP&nbsp;&nbsp;·&nbsp;&nbsp;NEVER FUND FLOW
      </p>
      {/* Desktop / tablet topology */}
      <div className="hidden md:block mt-[10px] md:max-lg:mt-[18px] md:max-lg:rounded-[22px] md:max-lg:bg-[#06060a] md:max-lg:border md:max-lg:border-[#222234] md:max-lg:px-[14px] md:max-lg:py-[18px]">
        <div className="relative" ref={dWrapRef}>
          <svg viewBox="0 0 666 440" className="w-full h-auto" role="group"
            onMouseLeave={onLeave}
            aria-label={`${name} deployment topology: ${dist.slice(0, 4).map((c: any) => `${c.chain} ${c.share_pct}%`).join(", ")}${others ? ` plus ${others} other deployment chains` : ""}, and the associated ${tok?.symbol || ""} token.`}>
            {rings(CENTER_D, [130, 180, 235], [0.22, 0.12, 0.07])}
            {dNodes.map((n, i) => edge(CENTER_D, n, i))}
            {tok && edge(CENTER_D, { ...TOKEN_D, accent: "#8b5cff" } as any, dNodes.length, true)}
            {center(CENTER_D, 63, false)}
            {dNodes.map((n, i) => (
              <Node key={n.chain} n={n} mobile={false} inView={inView} enterDelay={0.46 + i * 0.055}
                onHover={onHover} onLeave={onLeave} onCommit={onCommit}
                active={n.chain === active} dim={userActive && n.chain !== active} />
            ))}
            {tok && tokenNode(TOKEN_D, 27, false)}
          </svg>
          <AnimatePresence>
            {dPop && (
              <NetworkNodePopover key={dPop.key} containerRef={dWrapRef} viewBoxW={666}
                anchor={dPop.anchor} avoid={{ ...CENTER_D, r: 63 }} data={dPop.data} obstacles={dObstacles}
                onEnter={() => reHover(dPop.key)} onLeave={onLeave} />
            )}
          </AnimatePresence>
        </div>
        {others > 0 && (
          <p className="nl-geist text-center text-[8px] leading-[12px] font-semibold tracking-[0.42em] text-[#9696a8] mt-[2px] uppercase">
            {others} OTHER DEPLOYMENT CHAINS
          </p>
        )}
      </div>
      {/* Mobile topology — simplified, highest-priority relationships only */}
      <div className="md:hidden mt-[16px] rounded-[20px] bg-[#06060a] border border-[#222234] px-[10px] pt-[16px] pb-[10px]">
        <div className="relative" ref={mWrapRef}>
          <svg viewBox="0 0 358 360" className="w-full h-auto" role="group" onMouseLeave={onLeave}
            aria-label={`${name} deployment topology (mobile): top chains ${dist.slice(0, 4).map((c: any) => c.chain).join(", ")}${others ? ` plus ${others} more` : ""}.`}>
            {rings(CENTER_M, [80, 120], [0.18, 0.08])}
            {mNodes.map((n, i) => edge(CENTER_M, n, i))}
            {tok && edge(CENTER_M, { ...TOKEN_M, accent: "#8b5cff" } as any, mNodes.length, true)}
            {center(CENTER_M, 47, true)}
            {mNodes.map((n, i) => (
              <Node key={n.chain} n={n} mobile inView={inView} enterDelay={0.46 + i * 0.055}
                onHover={onHover} onLeave={onLeave} onCommit={onCommit}
                active={n.chain === active} dim={userActive && n.chain !== active} />
            ))}
            {tok && tokenNode(TOKEN_M, 21, true)}
          </svg>
          <AnimatePresence>
            {mPop && (
              <NetworkNodePopover key={mPop.key} containerRef={mWrapRef} viewBoxW={358}
                anchor={mPop.anchor} avoid={{ ...CENTER_M, r: 47 }} data={mPop.data} obstacles={mObstacles} compact
                onEnter={() => reHover(mPop.key)} onLeave={onLeave} />
            )}
          </AnimatePresence>
        </div>
        {others > 0 && (
          <p className="nl-geist text-center text-[7px] leading-[12px] font-semibold tracking-[0.42em] text-[#9696a8] uppercase">
            {others} OTHER DEPLOYMENT CHAINS
          </p>
        )}
      </div>
    </div>
  );
};
